import React, { useState, lazy, Suspense } from "react";
import heroDance from "../assets/hero-dance.webp";
import heroDanceMobile from "../assets/hero-dance-mobile.webp";
import heroMeditation from "../assets/hero-meditation.webp";
import heroSoundHealing from "../assets/hero-sound-healing.webp";
import Button from "../components/Buttons/Button";
import { Blurhash } from "react-blurhash";
import { Link } from "react-router-dom";

interface HeroProps {
  alignmentClasses: string;
}

const Hero: React.FC<HeroProps> = ({ alignmentClasses }) => {
  const [imagesLoaded, setImagesLoaded] = useState({
    dance: false,
    soundHealing: false,
    meditation: false,
  });


  // todo: remove this once we launch the site on production
  const handleRegisterClick = () => {
    window.open('https://docs.google.com/forms/d/e/1FAIpQLSdL_qHP46e1wtmBttDlSGlLDLhP56wNsYws1gAKLUK5HXWOHQ/viewform?usp=sharing', '_blank');
  };

  return (
    <div className={alignmentClasses + " mb-16 mt-16"}>
      {/* Hero Text - Full width, left aligned */}
      <h1 className="text-4xl md:text-6xl lg:text-6xl font-bold font-noto italic font-extralight my-[45px] md:my-[30px] text-left pb-4">
        Find wellness events near you
      </h1>

      {/* Grid Container */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-5">
        {/* Hero Dance Image */}
        <div className="relative col-span-2 md:col-span-3 md:row-span-2 lg:col-span-3 lg:row-span-2 h-[220px] rounded-[30px] overflow-hidden">
          <div className="absolute inset-0">
            <Blurhash
              hash="LOGbn}VDb{wI}=xbNHM_SNs.xZNI"
              width="100%"
              height="100%"
              resolutionX={16}
              resolutionY={16}
              punch={1}
              className={`w-full h-full ${
                imagesLoaded.dance ? 'animate-fade-out' : ''
              }`}
            />
            <img
              src={heroDance}
              srcSet={`
                ${heroDanceMobile} 400w,
                ${heroDance} 1200w
              `}
              sizes="(max-width: 480px) 400px,
                     (max-width: 768px) 800px,
                     1200px"
              alt="Dance"
              loading="eager"
              fetchPriority="high"
              width="800"
              height="450"
              decoding="async"
              className={`absolute inset-0 w-full h-full object-cover ${
                imagesLoaded.dance ? 'animate-fade-in' : 'opacity-0'
              }`}
              onLoad={() => setTimeout(() => 
                setImagesLoaded(prev => ({ ...prev, dance: true })), 100
              )}
            />
          </div>
        </div>

        {/* Paragraph Text */}
        <div className="col-span-1 md:col-span-2 md:row-start-3 lg:col-start-1 lg:col-span-2 lg:row-start-3 text-base">
          <h3 className="text-xl md:text-2xl lg:text-2xl font-bold mb-4">Simple ways to feel amazing, starting now.</h3>
          <p className="md:text-lg">
            {" "}
            From mindful breathing to sound healing and even sober raves, we
            bring wellness experiences right to you.
          </p>
        </div>

        {/* Sound Healing Image */}
        <div className="relative col-span-1 row-span-2 h-[160px] md:h-[180px] rounded-[30px] overflow-hidden">
          <div className="absolute inset-0">
            <Blurhash
              hash="LJL3}n00TKxt-SjFRkjY~qtQxCM|"
              width="100%"
              height="100%"
              resolutionX={32}
              resolutionY={32}
              punch={1}
              className={`w-full h-full ${
                imagesLoaded.soundHealing ? 'animate-fade-out' : ''
              }`}
            />
            <img
              src={heroSoundHealing}
              alt="Sound Healing"
              className={`absolute inset-0 w-full h-full object-cover ${
                imagesLoaded.soundHealing ? 'animate-fade-in' : 'opacity-0'
              }`}
              onLoad={() => setTimeout(() => 
                setImagesLoaded(prev => ({ ...prev, soundHealing: true })), 100
              )}
            />
          </div>
        </div>

        {/* Meditation Image */}
        <div className="relative hidden md:block md:col-span-1 md:col-start-4 md:row-span-2 md:row-start-2 h-[200px] rounded-[30px] overflow-hidden">
          <div className="absolute inset-0">
            <Blurhash
              hash="LKIr4^x^_N?vx[Rj9Fx]9]ofnNRj"
              width="100%"
              height="100%"
              resolutionX={32}
              resolutionY={32}
              punch={1}
              className={`w-full h-full ${
                imagesLoaded.meditation ? 'animate-fade-out' : ''
              }`}
            />
            <img
              src={heroMeditation}
              alt="Meditation"
              className={`absolute inset-0 w-full h-full object-cover ${
                imagesLoaded.meditation ? 'animate-fade-in' : 'opacity-0'
              }`}
              onLoad={() => setTimeout(() => 
                setImagesLoaded(prev => ({ ...prev, meditation: true })), 100
              )}
            />
          </div>
        </div>

        {/* CTA Button */}
        <div className="col-span-1 md:col-span-1 md:col-start-2 md:row-start-4 lg:col-start-3 lg:row-start-3 lg:col-span-1 flex items-start lg:items-end h-full">
          {process.env.REACT_APP_ENVIRONMENT !== "production" && (
            <Link to="/events">
              <Button>Find Events</Button>
            </Link>
          )}
          {process.env.REACT_APP_ENVIRONMENT === "production" && (
            <Button onClick={handleRegisterClick}>Join us</Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Hero;
